import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, ScanSearch } from "lucide-react";
import { axiosInstance } from "../App";

const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF, Html5QrcodeSupportedFormats.QR_CODE,
];

const ScannerView = ({ onScan, onError, compact }) => {
  const lastRef = useRef({ code: null, at: 0 });
  const busyRef = useRef(false);
  useEffect(() => {
    const scanner = new Html5Qrcode("camera-scanner-region", { formatsToSupport: FORMATS, verbose: false });
    let started = false;
    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: (w, h) => ({ width: Math.floor(w * 0.85), height: Math.floor(Math.min(h, w) * 0.45) }) },
      async (code) => {
        const now = Date.now();
        if (busyRef.current || (code === lastRef.current.code && now - lastRef.current.at < 2000)) return;
        busyRef.current = true;
        lastRef.current = { code, at: now };
        const ok = await onScan(code);
        if (ok && navigator.vibrate) navigator.vibrate(80);
        busyRef.current = false;
      },
      () => {}
    ).then(() => { started = true; }).catch((e) => onError(String(e?.message || e)));
    return () => { if (started) scanner.stop().then(() => scanner.clear()).catch(() => {}); };
  }, []);
  return <div id="camera-scanner-region" className={`w-full overflow-hidden rounded-lg bg-black ${compact ? "min-h-[200px] max-h-[40vh]" : "min-h-[260px]"}`} data-testid="camera-scanner-region" />;
};

export const PriceCheckDialog = ({ open, onOpenChange, boxId }) => {
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setError(""); setResult(null); } }, [open]);
  const handleScan = async (code) => {
    try {
      const { data } = await axiosInstance.get(`/inventory/products/code/${encodeURIComponent(code)}?box_id=${boxId}`);
      setResult({ code, product: data.product });
      return true;
    } catch (_e) {
      setResult({ code, product: null });
      return false;
    }
  };
  const p = result?.product;
  const available = p ? p.quantity - (p.reserved_quantity || 0) : 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4" data-testid="price-check-dialog">
        <DialogHeader>
          <DialogTitle>Consultar precio</DialogTitle>
          <DialogDescription>Apunta al código de barras. No se agrega al carrito.</DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" data-testid="price-check-error">
            No se pudo abrir la cámara. Revisa que el navegador tenga permiso para usarla. ({error})
          </p>
        ) : (
          open && <ScannerView onScan={handleScan} onError={setError} compact />
        )}
        {result && (p ? (
          <div className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4" data-testid="price-check-result">
            <p className="font-semibold text-slate-800 leading-tight" data-testid="price-check-name">{p.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{p.code}</p>
            <div className="flex items-end justify-between mt-2">
              <p className="text-3xl font-bold text-emerald-700" data-testid="price-check-price">₡{Number(p.price || 0).toLocaleString()}</p>
              <p className={`text-sm font-semibold ${available > 0 ? "text-slate-700" : "text-red-600"}`} data-testid="price-check-stock">
                {available > 0 ? `${available} disponibles` : "Sin existencias"}
                {(p.reserved_quantity || 0) > 0 && <span className="block text-xs font-normal text-slate-500 text-right">{p.reserved_quantity} apartados</span>}
              </p>
            </div>
          </div>
        ) : (
          <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="price-check-not-found">Código {result.code} no encontrado</p>
        ))}
        <Button onClick={() => onOpenChange(false)} className="h-12 w-full" data-testid="price-check-close">Listo</Button>
      </DialogContent>
    </Dialog>
  );
};

export const PriceCheckButton = ({ boxId, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className={className} data-testid="price-check-button" title="Consultar precio con la cámara">
        <ScanSearch className="w-5 h-5" />
      </Button>
      <PriceCheckDialog open={open} onOpenChange={setOpen} boxId={boxId} />
    </>
  );
};

export const CameraScanButton = ({ onScan, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const handleScan = async (code) => {
    const ok = await onScan(code);
    if (ok) setCount((c) => c + 1);
    return ok;
  };
  return (
    <>
      <Button type="button" variant="outline" onClick={() => { setError(""); setCount(0); setOpen(true); }} className={className} data-testid="camera-scan-button" title="Escanear con la cámara">
        <Camera className="w-5 h-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-4" data-testid="camera-scan-dialog">
          <DialogHeader>
            <DialogTitle>Escanear con cámara</DialogTitle>
            <DialogDescription>Apunta al código de barras. Cada producto leído se agrega al carrito.</DialogDescription>
          </DialogHeader>
          {error ? (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" data-testid="camera-scan-error">
              No se pudo abrir la cámara. Revisa que el navegador tenga permiso para usarla. ({error})
            </p>
          ) : (
            open && <ScannerView onScan={handleScan} onError={setError} />
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-emerald-700 font-medium" data-testid="camera-scan-count">{count} agregado{count === 1 ? "" : "s"}</span>
            <Button onClick={() => setOpen(false)} className="h-12 px-6" data-testid="camera-scan-close">Listo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
